import type { INavigation } from "@contentful-api/types/contentful";
import { CommonCMS } from "@lib/constants/contentful";
import { useMicrocopy } from "@lib/contentful/microcopy/MicrocopyContext";
import { Text } from "@ui/components/content/text/Text";
import { Box } from "@ui/components/layout/box/Box";
import { Flex } from "@ui/components/layout/flex/Flex";
import { Portal } from "@ui/components/layout/portal/Portal";
import { ECCOIcon } from "@ui/components/media-and-icons/ecco-icon/ECCOIcon";
import { space, transitions } from "@ui/design-tokens";
import { useDisclosure } from "@ui/hooks/useDisclosure";
import { useRouter } from "next/router";
import type { FC } from "react";
import { useEffect, useMemo, useState } from "react";
import RightNavigation from "../components/RightNavigation";
import { desktopNavItemWrapperStyles, rightContainerStyles } from "../styles";
import { AnimatedWrapper } from "./AnimatedWrapper";
import DesktopNavItem from "./DesktopNavItem";
import { styles } from "./DesktopNav.styles";
import { Button } from "@ui/components/forms/button/Button";
import { DESKTOP_OPEN_SEARCH_ICON } from "@e2e/helpers/navigation/constants";
import { isEmpty } from "lodash-es";

export interface DesktopNavProps {
    navigation: INavigation;
    onOpenSearchBar?: () => void;
    onCloseSearchBar?: () => void;
    searchBarOpen?: boolean;
}

export const DesktopNav: FC<DesktopNavProps> = ({
    navigation,
    onOpenSearchBar,
    onCloseSearchBar,
    searchBarOpen,
}) => {
    const { isOpen, onOpen, onClose } = useDisclosure();
    const { events } = useRouter();
    const [isHoveringNavBar, setIsHoveringNavBar] = useState<boolean>(false);
    const [heightNav, setHeightNav] = useState<number>(0);
    const [isChangingRoute, setIsChangingRoute] = useState(false);
    const [hoveredItemId, setHoveredItemId] = useState<string | null>(null);
    const { get: getMicrocopy } = useMicrocopy();

    const hoveredItem = useMemo(() => {
        return navigation?.fields?.navigationItems?.find((item) => item.sys.id === hoveredItemId);
    }, [hoveredItemId, navigation?.fields?.navigationItems]);

    const hoveredItemHasDropdown = useMemo(() => {
        if (!hoveredItem) return false;
        const { fields } = hoveredItem;
        const { linkCollections, thumbnailLinksTiles, thumbnailLinks, bottomLinkLabel } =
            fields || {};
        return Boolean(
            !isEmpty(linkCollections) ||
                !isEmpty(thumbnailLinksTiles) ||
                !isEmpty(thumbnailLinks) ||
                !isEmpty(bottomLinkLabel)
        );
    }, [hoveredItem]);

    // Track the router navigation so we can properly close stuff.
    useEffect(() => {
        const onRouteChangeStart = () => {
            onCloseSearchBar?.();
            onClose();
            setIsChangingRoute(true);
        };
        const onRouteChangeEnd = () => {
            setIsChangingRoute(false);
        };
        events.on("routeChangeStart", onRouteChangeStart);
        events.on("routeChangeComplete", onRouteChangeEnd);
        return () => {
            events.off("routeChangeStart", onRouteChangeStart);
            events.off("routeChangeComplete", onRouteChangeEnd);
        };
    }, [events, onClose, onCloseSearchBar, setIsChangingRoute]);

    // Determine if the user is using a mouse.
    useEffect(() => {
        const onMouseMove = () => {
            setIsHoveringNavBar(true);
        };
        document.addEventListener("mousemove", onMouseMove);
        return () => {
            document.removeEventListener("mousemove", onMouseMove);
        };
    }, []);

    return (
        <>
            <Flex
                onMouseEnter={isHoveringNavBar ? onOpen : undefined}
                onMouseLeave={() => {
                    onClose();
                    setHoveredItemId(null);
                }}
                direction="row"
                align="center"
                justifyContent="center"
                tabIndex={-1}
                data-testid="navPreSearchBox"
                sx={desktopNavItemWrapperStyles}
            >
                {navigation?.fields?.navigationItems?.map((navigationItem) => {
                    const { fields } = navigationItem;
                    const {
                        linkCollections,
                        thumbnailLinksTiles,
                        thumbnailLinks,
                        bottomLinkLabel,
                    } = fields || {};
                    const hasNestedContent = Boolean(
                        !isEmpty(linkCollections) ||
                            !isEmpty(thumbnailLinksTiles) ||
                            !isEmpty(thumbnailLinks) ||
                            !isEmpty(bottomLinkLabel)
                    );

                    return (
                        <DesktopNavItem
                            isOpen={isOpen}
                            key={navigationItem.sys.id}
                            navigationItem={navigationItem}
                            setHeightNav={setHeightNav}
                            heightNav={heightNav}
                            onOpen={isHoveringNavBar ? onOpen : undefined}
                            onClose={onClose}
                            isHoveringNavBar={isHoveringNavBar}
                            isSalesNavigationItem={
                                navigationItem.fields.isSalesNavigationItem || false
                            }
                            shouldRenderDropdown={hasNestedContent}
                            onItemHover={() => setHoveredItemId(navigationItem.sys.id)}
                        />
                    );
                })}
                <AnimatedWrapper isChangingRoute={isChangingRoute} height={heightNav} />
            </Flex>
            <Flex top={0} w="100%" gap={space["0.2"]} ml={0.2} justifyContent="flex-end" bg="white">
                <Flex margin={"0 0 0 0"} width={"auto"}>
                    <Button
                        data-testid={DESKTOP_OPEN_SEARCH_ICON}
                        cursor={searchBarOpen ? "initial" : "pointer"}
                        variant="unstyled"
                        {...styles.placeholder}
                        onClick={() => {
                            onOpenSearchBar?.();
                        }}
                    >
                        <ECCOIcon name="search" />
                        <Text
                            display={["block", "none", "none", "block", "block"]}
                            {...styles.placeHolderText}
                        >
                            {getMicrocopy(CommonCMS.shared, CommonCMS.searchBarPlaceholder)}
                        </Text>
                    </Button>
                </Flex>
            </Flex>
            <Flex
                sx={{
                    ...rightContainerStyles,
                }}
            >
                <RightNavigation />
            </Flex>
            <Portal>
                <Box
                    onClick={() => onCloseSearchBar?.()}
                    sx={{
                        position: "absolute",
                        backgroundColor: "rgba(0,0,0,0.6)",
                        opacity: (isOpen && hoveredItemHasDropdown) || searchBarOpen ? 1 : 0,
                        pointerEvents:
                            (isOpen && hoveredItemHasDropdown) || searchBarOpen ? "auto" : "none",
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        zIndex: 3,
                        transition: isChangingRoute
                            ? `opacity ${transitions.duration.faster} ${transitions.easing["ease-out"]}`
                            : `opacity ${transitions.duration.slow} ${transitions.easing["ease-out"]}`,
                    }}
                ></Box>
            </Portal>
        </>
    );
};
